import type { GridDataFormat } from './formats';

const TYPE_SETTINGS: readonly (keyof GridDataFormat)[] = [
  'type', 'formatKey', 'currency', 'cents', 'customPattern', 'options', 'optionsLink',
  'rollup', 'attachmentConfig', 'cells', 'invalid',
];

/** Heading-only storage must not replace a native cell format with its placeholder type. */
export function resolveColumnFormatBase(
  stored: GridDataFormat | undefined,
  native: GridDataFormat | undefined,
  inferredType: GridDataFormat['type'] = 'text',
): GridDataFormat {
  if (!stored) return native ?? { type: inferredType, headerOnly: true };
  if (!stored.headerOnly) return stored;
  return {
    ...(native ?? { type: inferredType }),
    ...(stored.headerMode === undefined ? {} : { headerMode: stored.headerMode }),
    ...(stored.headerAlign === undefined ? {} : { headerAlign: stored.headerAlign }),
    ...(stored.headerWrapMode === undefined ? {} : { headerWrapMode: stored.headerWrapMode }),
    headerOnly: true,
  };
}

/** Apply only the changed settings, retaining each selected column's other formats. */
export function patchColumnFormats(
  bases: Record<string, GridDataFormat>,
  fields: readonly string[],
  source: GridDataFormat,
  next: GridDataFormat,
  changedKeys?: readonly (keyof GridDataFormat)[],
  headingOnly = false,
): Record<string, GridDataFormat> {
  const typeOnly = Object.keys(next).length === 1 && Object.hasOwn(next, 'type');
  const keys = changedKeys ?? (typeOnly
    ? TYPE_SETTINGS
    : [...new Set([...Object.keys(source), ...Object.keys(next)] as (keyof GridDataFormat)[])]
      .filter((key) => JSON.stringify(source[key]) !== JSON.stringify(next[key])));

  return Object.fromEntries([...new Set(fields)].map((field) => {
    const base = bases[field] ?? { type: 'text' as const, headerOnly: true };
    const patched: GridDataFormat = { ...base };
    const writable = patched as unknown as Record<string, unknown>;
    for (const key of keys) {
      // The marker describes this target's stored cell formatting, never the source's.
      if (key === 'headerOnly') continue;
      if (Object.hasOwn(next, key)) writable[key] = next[key];
      else delete writable[key];
    }
    if (!headingOnly) delete patched.headerOnly;
    return [field, patched];
  }));
}
